import Inbox from '@mui/icons-material/Inbox'
import { Typography } from '@mui/material'
import {
  ListContext,
  useGetResourceLabel,
  useResourceContext,
  useResourceDefinition,
  useTranslate,
} from 'ra-core'
import * as React from 'react'
import { FC, useContext } from 'react'
import { CreateButton } from 'react-admin'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles({ name: 'RaEmpty' })((theme) => ({
  message: {
    textAlign: 'center',
    opacity: theme.palette.mode === 'light' ? 0.5 : 0.8,
    margin: '0 1em',
    color: theme.palette.mode === 'light' ? 'inherit' : theme.palette.text.primary,
  },
  icon: {
    width: '9em',
    height: '9em',
  },
  toolbar: {
    textAlign: 'center',
    marginTop: '2em',
  },
}))

const Empty: FC<EmptyProps> = (props) => {
  // <Empty> is used two ways: inside a list (EditAttachments, EditComments…),
  // where it picks between the datagrid and an empty state, and on plain form
  // pages (UpsertBoatAttributes, ShowBoatCategories), where there is no list at
  // all. react-admin 4's useListContext throws outside a provider, so read the
  // context directly and tolerate its absence. `basePath` is gone in v4 —
  // <CreateButton> derives its own link from the resource.
  const list = useContext(ListContext)
  const { hasCreate } = useResourceDefinition(props)
  const resource = useResourceContext(props)
  const { classes } = useStyles()
  const translate = useTranslate()
  const { icon = Inbox } = props
  // The resource's own create button, unless the page offers one already.
  const showCreate = hasCreate && props.create !== false

  const getResourceLabel = useGetResourceLabel()
  const resourceName = translate(`resources.${resource}.forcedCaseName`, {
    smart_count: 0,
    _: getResourceLabel(resource ?? '', 0),
  })

  const emptyMessageDefault = translate(`resources.${resource}.empty`, {
    _: translate('ra.page.empty', { name: resourceName }),
  })
  const inviteMessageDefault = translate(`resources.${resource}.invite`, {
    _: translate('ra.page.invite'),
  })
  const emptyMessage = props.message
    ? translate(props.message, { _: props.message })
    : emptyMessageDefault
  const inviteMessage = props.message
    ? translate(props.message, { _: props.message })
    : inviteMessageDefault

  // v4 dropped `ids` from the list context in favour of `data`.
  return (list?.data ?? props.ids ?? []).length ? (
    <>{props.children}</>
  ) : (
    <>
      <div className={classes.message}>
        <icon.type className={classes.icon} />
        <Typography variant='h4' sx={{ mb: 2 }}>
          {emptyMessage}
        </Typography>
        {showCreate && <Typography variant='body1'>{inviteMessage}</Typography>}
      </div>
      {showCreate && (
        <div className={classes.toolbar}>
          <CreateButton variant='contained' />
        </div>
      )}
    </>
  )
}

export interface EmptyProps {
  children?: React.ReactNode
  icon?: any
  /** Legacy: react-admin 3 spread `ids` from the list context. */
  ids?: unknown[]
  // ClassesOverride is gone in v4; tss-react exposes the same shape.
  classes?: Partial<ReturnType<typeof useStyles>['classes']>
  resource?: string
  message?: string
  /**
   * false: no create button of its own. The tabs of a detail page have their
   * "Erstellen" in the tab toolbar — which also links the new record to the
   * boat or customer; this one only opened the empty form.
   */
  create?: boolean
}

export default Empty
